/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { get as userGet } from 'js/lib/user';

import { Colors, Grid } from '@coursera/cds-core';

import ShoppingCartGridItem from 'bundles/composable-page-header-consumer/components/ShoppingCartGridItem';
import { useConsumerMyPageHeader } from 'bundles/composable-page-header-consumer/contexts/ConsumerMyPageHeaderContext';
import { useShoppingCart } from 'bundles/composable-page-header-consumer/contexts/ShoppingCartContext';
import MainNavigationWrapper from 'bundles/composable-page-header/components/MainNavigationWrapper';
import LanguageDropdownLegacyGridItem from 'bundles/composable-page-header/components/grid-items/LanguageDropdownLegacyGridItem';
import ProgramSwitcherGridItem from 'bundles/composable-page-header/components/grid-items/ProgramSwitcherGridItem';
import UnauthenticatedActionsGridItem from 'bundles/composable-page-header/components/grid-items/UnauthenticatedActionsGridItem';
import CoachStudioGridItem, {
  showCoachStudio,
} from 'bundles/composable-page-header/components/grid-items/coach/CoachStudioGridItem';
import CourseraHomeLogo from 'bundles/composable-page-header/components/grid-items/home-logo/CourseraHomeLogo';
import HomeLogoGridItemModule from 'bundles/composable-page-header/components/grid-items/home-logo/HomeLogoGridItemModule';
import { logInButtonProps, signUpButtonProps } from 'bundles/composable-page-header/constants';
import DefaultAccountDropdownGridItemRenderer from 'bundles/composable-page-header/module-renderers/DefaultAccountDropdownGridItemRenderer';
import LogoutOnlyAccountDropdownGridItemRenderer from 'bundles/composable-page-header/module-renderers/LogoutOnlyAccountDropdownGridItemRenderer';
import type { AuthButtonProps } from 'bundles/composable-page-header/types';
import GrowthDiscoveryEpicClient from 'bundles/epic/clients/GrowthDiscovery';

type Props = {
  customHeaderLogos?: Array<React.ReactNode>;
  acctDropdownLogoutOnly?: boolean;
  enableCourseraLogoLink?: boolean;
  showProgramSwitcher?: boolean;
  showShoppingCart?: boolean;
  showLanguageDropdown?: boolean;
  showCoach?: boolean;
  showAcctDropdown?: boolean;
  showSignupBtn?: boolean;
  showLoginBtn?: boolean;
};

const styles = {
  leftSide: css`
    width: auto;
  `,
  middleSide: css`
    flex-shrink: 1;
    flex-grow: 1;
    flex-basis: 0%;
    padding: 0 16px;
    width: 100%;
  `,
  rightSide: css`
    width: auto;
  `,
};

const ConsumerSimpleDesktopMainNav = ({
  customHeaderLogos = undefined,
  enableCourseraLogoLink = true,
  showProgramSwitcher = false,
  showShoppingCart = true,
  showLanguageDropdown = true,
  showCoach = true,
  acctDropdownLogoutOnly = false,
  showAcctDropdown = true,
  showSignupBtn = true,
  showLoginBtn = true,
}: Props) => {
  const {
    hasEnrolledPrograms,
    enrolledDegreePrograms,
    enrolledEnterprisePrograms,
    chosenDegree,
    chosenEnterpriseProgram,
    naptimeManagedThirdPartyOrganizations,
    naptimeChosenOrganization,
    naptimeOrganizationsWithProgramEnrollments,
    hasCheatingIncidents,
    canViewEducatorAdminLink,
    isCourseraPlusLearner,
    courseraPlusSubscriptionCycle,
  } = useConsumerMyPageHeader();
  const user = userGet();
  const shoppingCart = useShoppingCart();
  const isLoggedIn = user?.id;

  const unauthActionButtons: AuthButtonProps[] = [];
  if (showLoginBtn) unauthActionButtons.push(logInButtonProps());
  if (showSignupBtn) unauthActionButtons.push(signUpButtonProps());

  const isOrgScoped = GrowthDiscoveryEpicClient.preview('orgScopedHome');

  return (
    <MainNavigationWrapper>
      <Grid item container alignItems="center" spacing={16} css={styles.leftSide}>
        {isLoggedIn && hasEnrolledPrograms && showProgramSwitcher && !isOrgScoped && (
          <Grid item>
            <ProgramSwitcherGridItem
              currentProgram={chosenEnterpriseProgram}
              currentDegree={chosenDegree}
              programs={enrolledEnterprisePrograms}
              degrees={enrolledDegreePrograms}
            />
          </Grid>
        )}
        <Grid item>
          <HomeLogoGridItemModule
            href={enableCourseraLogoLink ? '/' : undefined}
            logos={
              customHeaderLogos?.length
                ? customHeaderLogos
                : [<CourseraHomeLogo key="1" logoColor={enableCourseraLogoLink ? undefined : Colors.gray[900]} />]
            }
          />
        </Grid>
      </Grid>
      <Grid item container alignItems="center" css={styles.middleSide} />
      <Grid item container alignItems="center" justifyContent="flex-end" wrap="nowrap" css={styles.rightSide}>
        {showShoppingCart && isLoggedIn && shoppingCart?.isValidCart && (
          <Grid item>
            <ShoppingCartGridItem />
          </Grid>
        )}
        {isLoggedIn && showLanguageDropdown && !isOrgScoped && (
          <Grid item>
            <LanguageDropdownLegacyGridItem
              currentThirdPartyOrgId={chosenEnterpriseProgram?.naptimeThirdPartyOrganization?.id}
            />
          </Grid>
        )}

        {isLoggedIn && showCoach && showCoachStudio() && (
          <Grid item>
            <CoachStudioGridItem />
          </Grid>
        )}
        {isLoggedIn ? (
          (acctDropdownLogoutOnly || showAcctDropdown) && (
            <Grid item>
              {acctDropdownLogoutOnly ? (
                <LogoutOnlyAccountDropdownGridItemRenderer isCourseraPlusLearner={!!isCourseraPlusLearner} />
              ) : (
                <DefaultAccountDropdownGridItemRenderer
                  hasEnrolledPrograms={!!hasEnrolledPrograms}
                  naptimeManagedThirdPartyOrganizations={naptimeManagedThirdPartyOrganizations}
                  hasCheatingIncidents={!!hasCheatingIncidents}
                  canViewEducatorAdminLink={!!canViewEducatorAdminLink}
                  isCourseraPlusLearner={!!isCourseraPlusLearner}
                  courseraPlusSubscriptionCycle={courseraPlusSubscriptionCycle}
                  organizations={naptimeOrganizationsWithProgramEnrollments}
                  degrees={enrolledDegreePrograms}
                  currentOrganization={naptimeChosenOrganization}
                  currentDegree={chosenDegree}
                />
              )}
            </Grid>
          )
        ) : (
          <Grid item container alignItems="center">
            <UnauthenticatedActionsGridItem buttons={unauthActionButtons} />
          </Grid>
        )}
      </Grid>
    </MainNavigationWrapper>
  );
};

export default ConsumerSimpleDesktopMainNav;
